import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Divider,
    FormHelperText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import CloseButton from "../../../Components/CloseButton";
import CustomIcon from "../../../Components/CustomIcon";
import {
    StyledDialog,
    StyledTextField,
} from "../../../Components/EditVehicleDetails/EditDialogShared";
import {
    CancelFormActionButton,
    PrimaryFormLoadingActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import {
    createNetworkEntity,
    updateNetworkEntity,
} from "../../../Api/NetworkEntityApi";
import { getOrgLocationMaster } from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import {
    mapServerErrors,
    normaliseForm,
    normaliseValue,
    validateForm,
    validateProperty,
} from "./validation";
import { FORM_TOKENS, WORK_ORDER_MENU } from "../WorkOrders/WorkOrderFormShared";

// Add / edit dialog for one network entity.
//
// Entirely config-driven: the fields, their labels, their types and their
// validation rules all come from the entity type's `properties`
// (orgMeta.AssetManagement.network.entityDefinition). Adding a field to an
// entity type needs no change here.
//
// Chrome is deliberately NOT bespoke. StyledDialog and StyledTextField are the
// same components the Edit Vehicle Details flow uses, and the label / asterisk /
// helper-text / dropdown treatments below mirror its views, so the two dialogs
// read as one product rather than two. Anything visual that needs changing
// should change in EditDialogShared, not here.

const LOCATION_LEVELS = ["level1", "level2", "level3", "level4", "level5"];

const locationLabel = (row) =>
    LOCATION_LEVELS.map((lv) => row?.[lv]).filter(Boolean).join(" / ");

// Copied from ChangeOriginLocationView rather than imported: that view keeps
// them file-local, and reaching into it for styling would couple this module to
// the vehicle flow's internals. The values must stay in step.
const AUTOCOMPLETE_INPUT_SX = {
    "& .MuiOutlinedInput-root": {
        fontSize: "14px",
        borderRadius: "8px",
        backgroundColor: FORM_TOKENS.surface,
        // The right padding is NOT decoration. MUI reserves room for the clear
        // and expand buttons by padding this root element, and a blanket
        // `padding: 4px 8px` override removes that reservation -- the value
        // then runs underneath the buttons and is clipped mid-character
        // instead of ellipsing. 56px is the two 28px icon buttons.
        padding: "4px 56px 4px 8px !important",
        "& fieldset": { borderColor: FORM_TOKENS.fieldBorder, borderWidth: "1px" },
        "&:hover fieldset": { borderColor: FORM_TOKENS.purple, borderWidth: "1px" },
        "&.Mui-focused fieldset": { borderColor: FORM_TOKENS.purple, borderWidth: "1px" },
        "& input": {
            fontSize: "14px",
            padding: "6px 4px !important",
            // Renders the "..." now that the padding above leaves visible space
            // for it. Applies while the field is unfocused; focused, the input
            // scrolls with the caret, which is what someone typing wants.
            textOverflow: "ellipsis",
        },
        "& input::placeholder": { color: FORM_TOKENS.placeholder, opacity: 1, fontSize: "14px" },
    },
};

// On Autocomplete rather than the TextField, so it beats MUI's absolutely
// positioned endAdornment default.
const AUTOCOMPLETE_SX = {
    "& .MuiAutocomplete-endAdornment": {
        top: "50% !important",
        transform: "translateY(-50%) !important",
    },
};

const DropdownPaper = (props) => (
    <Paper
        {...props}
        sx={{
            borderRadius: "8px",
            boxShadow: WORK_ORDER_MENU.shadow,
            "& .MuiAutocomplete-option": { fontSize: "14px" },
        }}
    />
);

// Label above the control, never a floating MUI label -- the required marker is
// a real red asterisk beside the text, matching every Edit Vehicle view.
const FieldLabel = ({ label, required }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography
            variant="fw400s14c000000"
            sx={{ lineHeight: "24px", color: FORM_TOKENS.inkBody }}
        >
            {label}
        </Typography>
        {required && (
            <Typography
                variant="fw400s14c000000"
                sx={{ fontWeight: 700, lineHeight: "24px", color: FORM_TOKENS.required }}
            >
                &nbsp;*
            </Typography>
        )}
    </Box>
);

const FieldHelper = ({ text, isError }) =>
    text ? (
        <FormHelperText
            sx={{
                fontSize: "10px",
                color: isError ? FORM_TOKENS.danger : FORM_TOKENS.inkFaint,
                ml: 0,
                mt: 0.25,
                // The full location path lands here, so it wraps rather than
                // being clipped a second time.
                overflowWrap: "anywhere",
            }}
        >
            {text}
        </FormHelperText>
    ) : null;

const CreateEditEntityDialog = ({ open, entityType, entity, onClose, onSuccess }) => {
    const isEdit = Boolean(entity?.id);
    const properties = useMemo(() => entityType?.properties || [], [entityType]);

    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [saving, setSaving] = useState(false);
    const [locations, setLocations] = useState([]);
    const [locationsError, setLocationsError] = useState("");
    const { openNotification } = useToast();

    const needsLocations = useMemo(
        () => properties.some((p) => p.type === "location"),
        [properties]
    );

    // Seed the form whenever the dialog opens. On edit the entity arrives as a
    // FLAT object -- core columns and custom attributes already merged by the
    // API -- so one lookup per property key works for both kinds.
    useEffect(() => {
        if (!open) return;
        const seeded = {};
        properties.forEach((property) => {
            const existing = entity?.[property.key];
            seeded[property.key] =
                existing !== undefined && existing !== null
                    ? existing
                    : (!isEdit && property.defaultValue !== undefined
                        ? property.defaultValue
                        : "");
        });
        setValues(seeded);
        setErrors({});
        setTouched({});
    }, [open, entity, properties, isEdit]);

    // The form can only PICK a location, never create one, so the list is
    // fetched once per open rather than searched server-side.
    useEffect(() => {
        if (!open || !needsLocations) return;
        let cancelled = false;
        setLocationsError("");
        (async () => {
            const res = await getOrgLocationMaster({});
            if (cancelled) return;
            // getOrgLocationMaster goes through GetData, which returns
            // {response, responseStatus} -- so rows sit under
            // res.response.data, and a failure must be distinguished from an
            // empty list or a 403 reads as "no locations configured".
            if (res?.responseStatus?.code === CODE.SUCCESS) {
                const rows = res?.response?.data || [];
                setLocations(Array.isArray(rows) ? rows : []);
            } else {
                setLocations([]);
                setLocationsError(
                    res?.responseStatus?.msg || "Could not load locations"
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, needsLocations]);

    const locationOptions = useMemo(
        () => locations.map((row) => ({ id: row.id, label: locationLabel(row) })),
        [locations]
    );

    const handleChange = useCallback((key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        // Clear the error as the user types; re-check on blur. Validating every
        // keystroke flags "Enter a valid email" on the first character.
        setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
    }, []);

    // Normalise on blur so the user sees exactly what will be stored, then
    // validate THAT value. Validating the closure's pre-normalisation value
    // meant a trimmed, lower-cased email was still checked with its original
    // spaces and failed the regex. No `values` dependency, so this is no longer
    // re-created on every keystroke.
    // A ref, not a `values` dependency, and not a setState inside another
    // setState updater. Updaters must be pure -- React runs them during render
    // and double-invokes them under StrictMode -- so the previous version, which
    // called setErrors from inside the setValues updater, was relying on that
    // side effect being idempotent. Reading the current values from a ref keeps
    // both setters at the top level and still avoids re-creating this callback
    // on every keystroke.
    const valuesRef = useRef(values);
    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    const handleBlur = useCallback((property) => {
        const key = property.key;
        const normalised = normaliseValue(property, valuesRef.current[key]);
        const next = normalised === null ? "" : normalised;

        setTouched((prev) => ({ ...prev, [key]: true }));
        setValues((prev) => ({ ...prev, [key]: next }));
        setErrors((prev) => ({ ...prev, [key]: validateProperty(property, next) }));
    }, []);

    const handleSubmit = async () => {
        const localErrors = validateForm(properties, values);
        if (Object.keys(localErrors).length) {
            setErrors(localErrors);
            setTouched(
                properties.reduce((acc, p) => ({ ...acc, [p.key]: true }), {})
            );
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...normaliseForm(properties, values),
                entityTypeCode: entityType.code,
            };
            const res = isEdit
                ? await updateNetworkEntity(entity.id, payload)
                : await createNetworkEntity(payload);

            if (
                res?.responseStatus?.code === CODE.SUCCESS ||
                res?.responseStatus?.code === CODE.URL_CREATED
            ) {
                openNotification({
                    text: `${entityType?.label || "Entity"} ${isEdit ? "updated" : "added"} successfully`,
                    icon: "toast-success",
                });
                // The saved record travels WITH the callback.
                //
                // The Network tab ignores it and refetches, which is right for
                // a table. The work order form cannot: it opens this dialog
                // from inside a destination picker and has to select the
                // customer that was just created, and hunting for it by name
                // in a refetched list is a guess. An extra argument is not a
                // breaking change -- a handler that takes none still works.
                onSuccess?.(res?.data || null);
                return;
            }

            // A 400 or 409 carries data.errors keyed by field. Painting them
            // onto the form is the whole reason this API keeps the response
            // body on failure -- see Api/NetworkEntityApi.js.
            const serverErrors = mapServerErrors(res);
            if (Object.keys(serverErrors).length) {
                setErrors(serverErrors);
                setTouched(
                    properties.reduce((acc, p) => ({ ...acc, [p.key]: true }), {})
                );
            } else {
                openNotification({
                    text: res?.responseStatus?.msg || "Could not save",
                    icon: "toast-error",
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const renderControl = (property) => {
        const { key, label, type = "text", required } = property;
        const error = touched[key] ? errors[key] || "" : "";
        const disabled = saving || (isEdit && property.immutable);

        if (type === "location" || type === "select") {
            const options =
                type === "location"
                    ? locationOptions
                    : (property.options || []).map((o) => ({
                        id: o.value,
                        label: o.label,
                    }));
            const selected = options.find((o) => o.id === values[key]) || null;

            return (
                <Autocomplete
                    fullWidth
                    disabled={disabled}
                    options={options}
                    value={selected}
                    getOptionLabel={(option) => option?.label ?? ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(_, option) => handleChange(key, option ? option.id : "")}
                    onBlur={() => handleBlur(property)}
                    sx={AUTOCOMPLETE_SX}
                    PaperComponent={DropdownPaper}
                    slotProps={{ listbox: { sx: { fontSize: "14px" } } }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={`Search or select ${label}`}
                            error={Boolean(error)}
                            // A five-level location path is longer than any
                            // half-width field; the native tooltip gives the
                            // whole value on hover without a wrapper that would
                            // fight the Autocomplete's own popper.
                            title={selected?.label || ""}
                            sx={AUTOCOMPLETE_INPUT_SX}
                        />
                    )}
                />
            );
        }

        return (
            <StyledTextField
                fullWidth
                autoComplete="off"
                variant="outlined"
                placeholder="Type here"
                type={type === "number" ? "number" : type === "date" ? "date" : "text"}
                multiline={type === "textarea"}
                minRows={type === "textarea" ? 3 : undefined}
                value={values[key] ?? ""}
                disabled={disabled}
                error={Boolean(error)}
                required={Boolean(required)}
                onChange={(e) => handleChange(key, e.target.value)}
                onBlur={() => handleBlur(property)}
            />
        );
    };

    const renderField = (property) => {
        const { key, label, type = "text", required, helpText } = property;
        const error = touched[key] ? errors[key] || "" : "";
        // The location dropdown owns two non-error messages of its own: it could
        // not load, or the org has none configured yet. Either explains an empty
        // list, so neither may be swallowed by the generic helpText.
        // A selected location is shown IN FULL under the field. The input can
        // only ever show its first ~40 characters, and the tail is the part
        // that identifies the place -- "India / South / Andaman and Nicobar
        // Islands / Port Blair" truncates to something several locations share.
        const selectedLocationLabel =
            type === "location"
                ? locationOptions.find((o) => o.id === values[key])?.label || ""
                : "";
        const locationNote =
            type === "location" && !error
                ? locationsError ||
                  selectedLocationLabel ||
                  (locationOptions.length ? "" : "No locations are configured yet")
                : "";
        const helper = error || locationNote || helpText || "";

        return (
            <Box
                key={key}
                sx={{
                    minWidth: 0,
                    // A textarea is the one control that reads badly at half
                    // width, so it spans the pair.
                    gridColumn: type === "textarea" ? { sm: "1 / -1" } : undefined,
                }}
            >
                <FieldLabel label={label} required={required} />
                {renderControl(property)}
                <FieldHelper text={helper} isError={Boolean(error)} />
            </Box>
        );
    };

    if (!entityType) return null;

    return (
        <StyledDialog open={open} onClose={() => !saving && onClose()}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CustomIcon name={isEdit ? "edit-icon" : "add-circle-icon"} />
                    <Typography
                        variant="fw600s16c000000"
                        sx={{ fontSize: "20px", lineHeight: "20px", color: FORM_TOKENS.inkStrong }}
                    >
                        {isEdit
                            ? `Edit ${entityType.label}`
                            : `Add ${entityType.label}`}
                    </Typography>
                </Box>
                <CloseButton onClick={() => !saving && onClose()} />
            </Box>

            <Divider sx={{ my: 3, borderColor: FORM_TOKENS.fieldBorder }} />

            {/* Two per row: a distributor declares eight properties, and a single
                column pushed the action buttons below the fold. */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    columnGap: 2,
                    rowGap: 2,
                    mb: 3,
                }}
            >
                {properties.map(renderField)}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <CancelFormActionButton
                    data-testid="network-entity-cancel"
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </CancelFormActionButton>
                <PrimaryFormLoadingActionButton
                    data-testid="network-entity-save"
                    onClick={handleSubmit}
                    loading={saving}
                    disabled={saving}
                >
                    {isEdit ? "Save Changes" : `Add ${entityType.label}`}
                </PrimaryFormLoadingActionButton>
            </Box>
        </StyledDialog>
    );
};

export default CreateEditEntityDialog;
